/**
 * Modern Bold · <mb-card-slider> custom element
 * ─────────────────────────────────────────────────────────────────────────
 * Collection-card image slider. Mobile: native scroll-snap swipe with a
 * dot pager and a one-time discovery nudge. Desktop (hover + fine
 * pointer): hovering the card previews image 2, arrows page through
 * the rest. Scrolling itself is pure CSS — this element only syncs the
 * pager, drives the arrows/hover, and eager-loads the lazy slides on
 * first interaction so paging never pops in.
 *
 * DOM contract (rendered by snippets/modern-collection-card.liquid):
 *   <mb-card-slider>
 *     a[aria-hidden] > [data-mb-track] > .mb-colcard__slide × N
 *     [data-mb-slider-prev] / [data-mb-slider-next]   arrows (aria-disabled
 *       at the ends — visual-only, buttons stay focusable per CLAUDE.md §2)
 *     .mb-colcard__dot × N                            passive pager
 */
if (!customElements.get('mb-card-slider')) {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const NUDGE_KEY = 'mb:card-slider-nudged';
  // Horizontal wheel delta that counts as a deliberate swipe, not jitter.
  const H_SWIPE_MIN = 12;
  // One card per page load may claim the nudge, before the sessionStorage
  // flag is set — the flag is only written once the nudge actually plays.
  let nudgeClaimed = false;

  class MBCardSlider extends HTMLElement {
    #cleanups = [];
    #track = null;
    #slides = [];
    #dots = [];
    #prevBtn = null;
    #nextBtn = null;
    #index = 0;
    /* Where the current programmatic scroll is headed. Paging decisions
       read THIS, not #index: mid-animation the position rounds to the
       old slide, so rapid arrow clicks based on #index all target the
       same slide and a double-click only advanced one image. */
    #pendingTarget = null;
    #userNavigated = false;
    #imagesEager = false;
    #raf = 0;
    #io = null;

    connectedCallback() {
      this.#track = this.querySelector('[data-mb-track]');
      this.#slides = [...this.querySelectorAll('.mb-colcard__slide')];
      this.#dots = [...this.querySelectorAll('.mb-colcard__dot')];
      this.#prevBtn = this.querySelector('[data-mb-slider-prev]');
      this.#nextBtn = this.querySelector('[data-mb-slider-next]');
      if (!this.#track || this.#slides.length < 2) return;

      this.#listen(this.#track, 'scroll', () => {
        if (this.#raf) return;
        this.#raf = requestAnimationFrame(() => {
          this.#raf = 0;
          this.#sync();
        });
      }, { passive: true });

      this.#listen(this.#prevBtn, 'click', () => this.#page(-1));
      this.#listen(this.#nextBtn, 'click', () => this.#page(1));
      // A mouse press must not move focus into the arrows: lingering focus
      // keeps the card :focus-within, so the arrows stayed visible after
      // the pointer left. preventDefault on mousedown skips the focus
      // transfer while click still fires; keyboard Tab is untouched.
      const noFocusSteal = (e) => e.preventDefault();
      this.#listen(this.#prevBtn, 'mousedown', noFocusSteal);
      this.#listen(this.#nextBtn, 'mousedown', noFocusSteal);

      if (finePointer.matches) {
        // Hover preview mirrors the old CSS hover-swap: in shows image 2,
        // out returns to 1 — but never fights a slide the user picked.
        // Intent-based on purpose: a fast hover-out fires while the smooth
        // scroll to image 2 is still mid-flight, so #index proves nothing
        // (checking it left cards stuck on image 2). Unless the user chose
        // a slide themselves, enter ALWAYS steers to 1 and leave to 0 —
        // scrollTo() mid-animation just redirects the ride.
        const card = this.closest('.mb-colcard') || this;
        this.#listen(card, 'mouseenter', () => {
          this.#eagerLoad();
          if (!this.#userNavigated) this.#go(1);
        });
        this.#listen(card, 'mouseleave', () => {
          if (!this.#userNavigated) this.#go(0);
        });
        // Horizontal trackpad scroll or a touch on a hybrid laptop = the
        // user picked a slide themselves; stop steering it from hover.
        // The latch is permanent, so it needs real horizontal intent: a
        // trackpad's vertical momentum tail fires deltaX:1/deltaY:0, which
        // beat a bare deltaX > deltaY test and left the card stuck on
        // image 2 the moment the pointer walked away.
        this.#listen(this.#track, 'wheel', (e) => {
          const dx = Math.abs(e.deltaX);
          if (dx >= H_SWIPE_MIN && dx > Math.abs(e.deltaY) * 2) {
            this.#userNavigated = true;
            this.#pendingTarget = null; // user took over mid-flight
          }
        }, { passive: true });
        this.#listen(this.#track, 'touchstart', () => {
          this.#eagerLoad();
          this.#userNavigated = true;
          this.#pendingTarget = null;
        }, { passive: true });
      } else {
        this.#listen(this.#track, 'touchstart', () => {
          this.#eagerLoad();
          this.#cancelNudge();
        }, { passive: true });
        this.#maybeNudge();
      }

      this.#sync();
    }

    disconnectedCallback() {
      this.#cleanups.forEach((fn) => fn());
      this.#cleanups = [];
      this.#io?.disconnect();
      this.#io = null;
      if (this.#raf) cancelAnimationFrame(this.#raf);
      this.#raf = 0;
    }

    #listen(el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts);
      this.#cleanups.push(() => el.removeEventListener(type, fn, opts));
    }

    #page(dir) {
      const base = this.#pendingTarget ?? this.#index;
      const target = Math.min(Math.max(base + dir, 0), this.#slides.length - 1);
      if (target === base) return; // aria-disabled end — no-op
      this.#userNavigated = true;
      this.#eagerLoad();
      this.#go(target);
    }

    #go(i) {
      this.#pendingTarget = i;
      this.#track.scrollTo({
        left: i * this.#track.clientWidth,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    #sync() {
      const width = this.#track.clientWidth || 1;
      this.#index = Math.min(
        this.#slides.length - 1,
        Math.max(0, Math.round(this.#track.scrollLeft / width))
      );
      if (this.#pendingTarget !== null && this.#index === this.#pendingTarget) this.#pendingTarget = null;
      this.#dots.forEach((dot, i) => dot.classList.toggle('is-active', i === this.#index));
      this.#prevBtn?.setAttribute('aria-disabled', String(this.#index === 0));
      this.#nextBtn?.setAttribute('aria-disabled', String(this.#index === this.#slides.length - 1));
    }

    #eagerLoad() {
      if (this.#imagesEager) return;
      this.#imagesEager = true;
      this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    /* One-time swipe hint: once per session, when the first card is well
       inside the viewport, the slides pull left and spring back to flash
       the edge of image 2. Touch devices only — desktop has hover+arrows. */
    #maybeNudge() {
      let seen = false;
      try {
        seen = sessionStorage.getItem(NUDGE_KEY) === '1';
      } catch {
        /* storage blocked — treat as unseen, worst case it replays */
      }
      if (nudgeClaimed || seen || reducedMotion.matches) return;
      nudgeClaimed = true;
      this.#io = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#io?.disconnect();
        this.#io = null;
        try {
          sessionStorage.setItem(NUDGE_KEY, '1');
        } catch {
          /* non-fatal */
        }
        // already swiped before the hint fired — they've discovered it
        if (this.#track.scrollLeft > 8) return;
        this.#eagerLoad(); // image 2 peeks during the nudge — no blank flash
        this.#track.classList.add('is-nudging');
        this.#listen(this.#track, 'animationend', () => this.#cancelNudge(), { once: true });
      }, { threshold: 0.6 });
      this.#io.observe(this);
    }

    #cancelNudge() {
      this.#track?.classList.remove('is-nudging');
    }
  }

  customElements.define('mb-card-slider', MBCardSlider);
}
